import { datadogRum } from '@datadog/browser-rum';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { activeSessionIdAtom } from '@/atoms/liveTranscriptionAtom';
import { useIsMiniModeEnabled } from '@/hooks/useIsMiniModeEnabled';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type { MiniModeCloseReason } from '@/models/analytics/features/mini-mode';
import { logger } from '@/utils/logger/logger';

import { track } from '@/lib/analytics/track';
import { useAutoMediaSession } from './hooks/useAutoMediaSession';
import { usePipRumBridge } from './hooks/usePipRumBridge';
import { PictureInPictureContext } from './picture-in-picture-context';
import {
  getPipOpenRejectionContext,
  isExpectedPipOpenRejection,
} from './picture-in-picture-errors';

import type {
  PictureInPictureContextValue,
  PictureInPictureProviderProps,
  PipOpenOptions,
} from './picture-in-picture-provider.types';

const DEFAULT_PIP_WIDTH = 480;
const DEFAULT_PIP_HEIGHT = 260;

/**
 * Chrome launched under automation (`navigator.webdriver` — Playwright,
 * Selenium, `--enable-automation`) draws a "controlled by automated test
 * software" infobar inside the PiP window. `requestWindow`'s height sizes the
 * window as if no infobar existed and the bar is then carved out of the inner
 * viewport, squeezing the content (measured: 56px on Chrome 150 / macOS @1x).
 * Pre-size the request so the viewport still ends up at the intended height;
 * if the estimate is ever off, the mini-mode scroll fallback keeps controls
 * reachable. No equivalent web-exposed signal exists for the debugger-attach
 * infobar, which can appear mid-session.
 */
const AUTOMATION_INFOBAR_HEIGHT = 56;

const getAutomationBannerAllowance = () =>
  navigator.webdriver ? AUTOMATION_INFOBAR_HEIGHT : 0;

function isStyleNode(node: Node): node is HTMLStyleElement | HTMLLinkElement {
  return (
    node instanceof HTMLStyleElement ||
    (node instanceof HTMLLinkElement && node.rel === 'stylesheet')
  );
}

/** Clone all existing stylesheets from the source document head into the target. */
function cloneExistingStyles(
  sourceDoc: Document,
  targetDoc: Document,
  cloneMap: Map<Node, Node>
) {
  const styleNodes = sourceDoc.head.querySelectorAll('style, link[rel="stylesheet"]');
  styleNodes.forEach((node) => {
    const clone = node.cloneNode(true);
    cloneMap.set(node, clone);
    targetDoc.head.appendChild(clone);
  });
}

/**
 * Observe the source document head for dynamically injected styles (e.g. Next.js styled-jsx)
 * and mirror additions/removals into the target document.
 */
function observeStyleChanges(
  sourceDoc: Document,
  targetDoc: Document,
  cloneMap: Map<Node, Node>
): MutationObserver {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (isStyleNode(node)) {
          const clone = node.cloneNode(true);
          cloneMap.set(node, clone);
          targetDoc.head.appendChild(clone);
        }
      }
      for (const node of mutation.removedNodes) {
        const clone = cloneMap.get(node);
        if (clone?.parentNode) {
          clone.parentNode.removeChild(clone);
          cloneMap.delete(node);
        }
      }
    }
  });

  observer.observe(sourceDoc.head, { childList: true });
  return observer;
}

/**
 * Mirror the class attribute from the source <html> element to the target <html>.
 * This keeps the theme class (e.g. "newBrand") in sync when the user switches themes.
 */
function observeThemeChanges(sourceDoc: Document, targetDoc: Document): MutationObserver {
  targetDoc.documentElement.className = sourceDoc.documentElement.className;

  const observer = new MutationObserver(() => {
    targetDoc.documentElement.className = sourceDoc.documentElement.className;
  });

  observer.observe(sourceDoc.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  });

  return observer;
}

/** Copy font variable classes from the main <main> element onto the PiP body. */
function applyFontClasses(sourceDoc: Document, targetDoc: Document) {
  const mainEl = sourceDoc.querySelector('main');
  if (mainEl) {
    targetDoc.body.className = mainEl.className;
  }
}

export function PictureInPictureProvider({ children }: PictureInPictureProviderProps) {
  const isMiniModeEnabled = useIsMiniModeEnabled();
  const activeSessionId = useAtomValue(activeSessionIdAtom);
  const activeSessionIdRef = useRef<string | null>(activeSessionId);
  activeSessionIdRef.current = activeSessionId;

  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  // Mirrors pipWindow state so the unmount cleanup can close the window without stale captures
  const pipWindowRef = useRef<Window | null>(null);
  const styleObserverRef = useRef<MutationObserver | null>(null);
  const themeObserverRef = useRef<MutationObserver | null>(null);
  const cloneMapRef = useRef<Map<Node, Node>>(new Map());

  // Analytics state captured at open time, read at close time.
  const openedAtRef = useRef<number | null>(null);
  const sessionAtOpenRef = useRef<string | null>(null);
  // Carries the caller's close reason into the pagehide listener in case
  // `pipWindow.close()` ends up firing pagehide before `cleanup(reason)` runs.
  // Defends against the (rare) case where the browser dispatches pagehide
  // synchronously from `close()`.
  const pendingCloseReasonRef = useRef<MiniModeCloseReason | null>(null);

  const isPipSupported = useMemo(() => {
    if (!isMiniModeEnabled) return false;
    if (typeof window === 'undefined') return false;
    return 'documentPictureInPicture' in window;
  }, [isMiniModeEnabled]);

  const isPipOpen = pipWindow !== null;

  const cleanup = useCallback((closeReason: MiniModeCloseReason = 'programmatic') => {
    styleObserverRef.current?.disconnect();
    styleObserverRef.current = null;
    themeObserverRef.current?.disconnect();
    themeObserverRef.current = null;
    cloneMapRef.current.clear();

    if (openedAtRef.current !== null) {
      track({
        event_name: TrackingEventName.MiniModeClosed,
        props: {
          close_reason: closeReason,
          duration_ms: Date.now() - openedAtRef.current,
          session_id: sessionAtOpenRef.current,
        },
      });
      openedAtRef.current = null;
      sessionAtOpenRef.current = null;
    }

    setPipWindow(null);
  }, []);

  const openPip = useCallback(
    async (options?: PipOpenOptions) => {
      if (!isPipSupported) return;

      // Close any existing PiP window first
      pipWindow?.close();
      cleanup();

      try {
        const pip = await window.documentPictureInPicture!.requestWindow({
          width: options?.width ?? DEFAULT_PIP_WIDTH,
          height:
            (options?.height ?? DEFAULT_PIP_HEIGHT) + getAutomationBannerAllowance(),
          disallowReturnToOpener: options?.disallowReturnToOpener ?? true,
        });

        const cloneMap = cloneMapRef.current;

        // 1. Clone existing stylesheets
        cloneExistingStyles(document, pip.document, cloneMap);

        // 2. Watch for dynamically injected styles (Next.js styled-jsx, CSS modules)
        styleObserverRef.current = observeStyleChanges(document, pip.document, cloneMap);

        // 3. Mirror the theme class on <html> and keep it in sync
        themeObserverRef.current = observeThemeChanges(document, pip.document);

        // 4. Apply font variable classes from <main> to the PiP body
        applyFontClasses(document, pip.document);

        // 5. Black window background so the pill widget floats over it
        pip.document.body.style.background = '#000';

        // 6. Clean up when user closes the PiP window via the X button.
        // Update the ref synchronously so the stale pagehide handler from a
        // just-closed window sees the newer reference and no-ops instead of
        // wiping the freshly opened window's state.
        pipWindowRef.current = pip;

        pip.addEventListener('pagehide', () => {
          if (pipWindowRef.current === pip) {
            const reason = pendingCloseReasonRef.current ?? 'window_closed';
            pendingCloseReasonRef.current = null;
            cleanup(reason);
          }
        });

        openedAtRef.current = Date.now();
        sessionAtOpenRef.current = activeSessionIdRef.current;
        track({
          event_name: TrackingEventName.MiniModeOpened,
          props: {
            trigger: options?.trigger ?? 'manual',
            session_id: activeSessionIdRef.current,
          },
        });

        setPipWindow(pip);
      } catch (error) {
        const rejectionContext = getPipOpenRejectionContext(error);
        if (isExpectedPipOpenRejection(error)) {
          logger.debug(
            '[PictureInPicture] PiP window open request was rejected by the browser',
            rejectionContext
          );
          // Expected rejections are debug-only (dropped in prod logs), so emit a
          // low-cardinality RUM action as a tripwire — lets us alert on a spike
          // without re-flooding the logs index. No-op when RUM is uninitialised
          // (e.g. on-prem), matching every other datadogRum.* callsite.
          datadogRum.addAction('pip_open_rejected', {
            reason: rejectionContext.errorName ?? 'unknown',
          });
        } else {
          // Pass the extracted context, not the raw error: DOMException/Error
          // props are non-enumerable and serialise to "[DOMException]" in
          // Datadog, losing the name we need to triage unexpected failures.
          logger.error('[PictureInPicture] Failed to open PiP window', rejectionContext);
        }
        cleanup();
      }
    },
    [isPipSupported, pipWindow, cleanup]
  );

  const closePip = useCallback(
    (reason: MiniModeCloseReason = 'programmatic') => {
      pendingCloseReasonRef.current = reason;
      pipWindow?.close();
      // No-op if pagehide ran synchronously inside close() and already fired
      // the event; otherwise this is the path that fires mini_mode_closed.
      cleanup(reason);
    },
    [pipWindow, cleanup]
  );

  // Keep the ref in sync so unmount cleanup has access to the latest window
  useEffect(() => {
    pipWindowRef.current = pipWindow;
  }, [pipWindow]);

  // Route the unmount teardown (logout, full page nav) through the latest
  // `cleanup` so `mini_mode_closed` fires. A ref avoids re-binding the effect
  // on every callback re-create, which would break mount-only semantics.
  const cleanupRef = useRef(cleanup);
  cleanupRef.current = cleanup;

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    return () => {
      pipWindowRef.current?.close();
      cleanupRef.current('programmatic');
    };
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  useAutoMediaSession({ openPip, closePip, isPipOpen });
  usePipRumBridge({ pipWindow });

  const value = useMemo<PictureInPictureContextValue>(
    () => ({
      isPipSupported,
      isPipOpen,
      pipWindow,
      openPip,
      closePip,
    }),
    [isPipSupported, isPipOpen, pipWindow, openPip, closePip]
  );

  return (
    <PictureInPictureContext.Provider value={value}>
      {children}
    </PictureInPictureContext.Provider>
  );
}
